/// <reference types="react-dom" />

import React = require("react");
import ReactDOM = require("react-dom");

import { css } from "VSSUI/Util";
import { Link } from "OfficeFabric/Link";
import { Spinner, SpinnerSize } from "OfficeFabric/Spinner";

import Component_Base = require("VSS/Flux/Component");

import DistributedTask = require("TFS/DistributedTask/Contracts");
import DistributedTaskApi = require("TFS/DistributedTask/TaskAgentRestClient");
import DTConstants = require("Presentation/Scripts/TFS/Generated/TFS.DistributedTask.Constants");
import Resources = require("Build/Scripts/Resources/TFS.Resources.Build");
import TFS_Host_TfsContext = require("Presentation/Scripts/TFS/TFS.Host.TfsContext");

import { ViewInprogressJobsView } from "Build/Scripts/AdminBuildAndRelease/Components/ViewInprogressJobsView";
import { Component as InfoButton } from "DistributedTaskControls/Components/InfoButton";
import { ICalloutContentProps } from "DistributedTaskControls/Components/CalloutComponent";

import * as Utils_String from "VSS/Utils/String";
import * as Utils_Url from "VSS/Utils/Url";
import * as Service from "VSS/Service";
import * as Contribution_Services from "VSS/Contributions/Services";

import "VSS/LoaderPlugins/Css!Build/Scripts/AdminBuildAndRelease/Components/AdminConcurrentJobsView";

export interface IProps extends Component_Base.Props {
}

export interface Concurrency {
    parallelismTag: string;
    isHosted: boolean;
    totalConcurrentJobsCount: number;
    freeConcurrentJobsCount: number;
    visualStudioEnterpriseUsersConcurrentJobsCount: number;
    purchasedConcurrentJobsCount: number;
    totalFreeMinutes: number;
    consumedFreeMinutes: number;
}

export interface ConcurrentJobsState extends Component_Base.State {
    publicProjectsMicrosoftHostedConcurrency?: Concurrency;
    publicProjectsSelfHostedConcurrency?: Concurrency;
    taskHubLicenseDetails: DistributedTask.TaskHubLicenseDetails;
    loadingPrivateMarketplaceData: boolean;
}

// data from BuildQueueDataProvider
export interface ParallelJobsData {
    buildQueueOptions: string;
    includeResourceLimitsSection: boolean;
    includeConcurrentJobsSection: boolean;
    resourceUsages: DistributedTask.ResourceUsage[];
    taskHubLicenseDetails: DistributedTask.TaskHubLicenseDetails;
    selfHostedLicensePurchaseLink: string;
    microsoftHostedLicensePurchaseLink: string;
}

const DATA_PROVIDER_CONTRIBUTION_KEY: string = "ms.vss-build-web.build-queue-hub-data-provider";

export class AdminConcurrentJobsView extends Component_Base.Component<IProps, ConcurrentJobsState> {
    constructor(props: IProps) {
        super(props);

        let tfsContext: TFS_Host_TfsContext.TfsContext = TFS_Host_TfsContext.TfsContext.getDefault();
        let tfsConnection: Service.VssConnection = new Service.VssConnection(tfsContext.contextData);
        this._httpClient = tfsConnection.getHttpClient<DistributedTaskApi.TaskAgentHttpClient>(DistributedTaskApi.TaskAgentHttpClient);

        let collectionUrl: string = tfsContext.contextData.collection.uri;
        this._privateProjectSelftHostedPurchaseLink = this._getBillingUrl(collectionUrl);
        this._privateProjectsMicrosoftHostedPurchaseLink = this._getBillingUrl(collectionUrl);

        this._microsoftHostedImageUrl = Utils_String.format("{0}{1}/{2}", tfsContext.configuration.getResourcesPath(), "Build", encodeURIComponent("concurrency-icon-microsoft-hosted.png"));
        this._selfHostedImageUrl = Utils_String.format("{0}{1}/{2}", tfsContext.configuration.getResourcesPath(), "Build", encodeURIComponent("concurrency-icon-self-hosted.png"));

        const pageDataService = Service.getService(Contribution_Services.WebPageDataService);
        const pageData = pageDataService.getPageData<ParallelJobsData>(DATA_PROVIDER_CONTRIBUTION_KEY);

        this.state = {
            taskHubLicenseDetails: pageData.taskHubLicenseDetails,
            loadingPrivateMarketplaceData: true
        };
    }

    public componentDidMount(): void {
        this._loadMarketplaceData();
        this._loadPublicResourceUsages();

        let queryParameters: IDictionaryStringTo<string> = Utils_Url.getQueryParameters(window.location.href);
        // if "isHosted" is true, agent pool is MS-hosted, otherwise self-hosted
        let isHosted = queryParameters[this._isHosted] && Utils_String.equals(queryParameters[this._isHosted], true + "", true);
        let parallelismTag = queryParameters[this._parallelismTag];
        if (isHosted !== undefined && parallelismTag) {
            this._showConcurrencyPanel(isHosted, parallelismTag);
        }
    }

    private async _loadMarketplaceData() {
        const pageDataService = Service.getService(Contribution_Services.WebPageDataService);
        const fullData = await pageDataService.getDataAsync<ParallelJobsData>(DATA_PROVIDER_CONTRIBUTION_KEY);

        this.setState({
            taskHubLicenseDetails: fullData.taskHubLicenseDetails,
            loadingPrivateMarketplaceData: false
        })
    }

    private async _loadPublicResourceUsages() {
        let publicProjectsMicrosoftHostedConcurrency: Concurrency = null;
        let publicProjectsSelfHostedConcurrency: Concurrency = null;

        const pageDataService = Service.getService(Contribution_Services.WebPageDataService);
        const resourceData = await pageDataService.getDataAsync<ParallelJobsData>(DATA_PROVIDER_CONTRIBUTION_KEY, undefined, {FetchPublicResourceUsage: true});;

        if (resourceData.resourceUsages) {
            resourceData.resourceUsages.forEach((resourceUsage: DistributedTask.ResourceUsage) => {
                if (resourceUsage && resourceUsage.resourceLimit) {
                    let freeJobsCount = 0;
                    let purchasedJobsCount = 0;
                    let visualStudioEnterpriseUsersCount = 0;
                    if (resourceUsage.resourceLimit.resourceLimitsData) {
                        if (resourceUsage.resourceLimit.resourceLimitsData[DTConstants.ResourceLimitConstants.FreeCount]) {
                            freeJobsCount = parseInt(resourceUsage.resourceLimit.resourceLimitsData[DTConstants.ResourceLimitConstants.FreeCount]);
                        }

                        if (resourceUsage.resourceLimit.resourceLimitsData[DTConstants.ResourceLimitConstants.PurchasedCount]) {
                            purchasedJobsCount = parseInt(resourceUsage.resourceLimit.resourceLimitsData[DTConstants.ResourceLimitConstants.PurchasedCount]);
                        }

                        if (resourceUsage.resourceLimit.resourceLimitsData[DTConstants.ResourceLimitConstants.EnterpriseUsersCount]) {
                            visualStudioEnterpriseUsersCount = parseInt(resourceUsage.resourceLimit.resourceLimitsData[DTConstants.ResourceLimitConstants.EnterpriseUsersCount]);
                        }
                    }

                    let concurrency: Concurrency = {
                        parallelismTag: resourceUsage.resourceLimit.parallelismTag,
                        isHosted: resourceUsage.resourceLimit.isHosted,
                        totalConcurrentJobsCount: resourceUsage.resourceLimit.totalCount,
                        freeConcurrentJobsCount: freeJobsCount,
                        visualStudioEnterpriseUsersConcurrentJobsCount: visualStudioEnterpriseUsersCount,
                        consumedFreeMinutes: resourceUsage.usedMinutes,
                        totalFreeMinutes: resourceUsage.resourceLimit.totalMinutes,
                        purchasedConcurrentJobsCount: purchasedJobsCount
                    };

                    if (Utils_String.equals(concurrency.parallelismTag, DTConstants.ParallelismTagTypes.Public, true) && concurrency.isHosted) {
                        publicProjectsMicrosoftHostedConcurrency = concurrency;
                    }
                    else if (Utils_String.equals(concurrency.parallelismTag, DTConstants.ParallelismTagTypes.Public, true) && !concurrency.isHosted) {
                        publicProjectsSelfHostedConcurrency = concurrency;
                    }
                }
            });
        }

        this.setState({
            publicProjectsMicrosoftHostedConcurrency,
            publicProjectsSelfHostedConcurrency
        });
    }

    public render(): JSX.Element {
        return (<div className="admin-concurrency-jobs-view">
                    <header>
                        <label className={css('private-concurrency-title')} role="heading" aria-level={1}>{Resources.PrivateProjectsLabel}</label>
                    </header>
                    { 
                        this._getPrivateProjectsMicrosoftHostedConcurrencyView() 
                    }
                    {
                        this._getPrivateProjectsSelfHostedConcurrencyView()
                    }
                    {
                        this._getPublicProjectsSection()
                    }
                </div>);
    }

    private _getPublicProjectsSection(): JSX.Element {
        return (<div>
                    <div className={css('public-concurrency-title-section')}>
                        <header>
                            <label className={css('public-concurrency-title')} role="heading" aria-level={1}>{Resources.PublicProjectsLabel}</label>
                        </header>
                    </div>
                    { this._getPublicProjectsMicrosoftHostedConcurrencyView() }
                    { this._getPublicProjectsSelfHostedConcurrencyView() }
                </div>);
    }

    private _getPrivateProjectsMicrosoftHostedConcurrencyView(): JSX.Element {
        const concurrency = this.state.taskHubLicenseDetails;
        const showFreeTier = !concurrency.hostedLicensesArePremium && !this.state.loadingPrivateMarketplaceData && concurrency.totalHostedLicenseCount > 0;
        return (<div>
                    <div className={css('concurrency-row title-row')}>
                        <div className={css('title-header-section')}>
                            <img className="concurrency-type-icon" src={this._microsoftHostedImageUrl} alt="" aria-label={Resources.MicrosoftHostedConcurrencyTitle} />
                            <div className={css('title-and-link-section')}>
                                <div className={css('title-section')}>
                                    <label className={css('large-font')}>{Resources.MicrosoftHostedConcurrencyTitle}</label>
                                    <InfoButton
                                        isIconFocusable={true}
                                        calloutContent={{
                                                            calloutDescription: Resources.MicrosoftHostedConcurrencyInfoText,
                                                            calloutLink: this._microsoftHostedLearnMoreLink,
                                                            calloutLinkText: Resources.LearnMoreText
                                                        } as ICalloutContentProps} />
                                </div>
                                <Link className={css('small-font view-inprogress-jobs-link')} onClick={(event: React.MouseEvent<HTMLElement>) =>
                                    this._showPrivateProjectsMicrosoftHostedConcurrencyPanel(event.currentTarget)}>{Resources.ViewInProgressJobsText}</Link>
                            </div>
                        </div>
                        {
                            showFreeTier &&
                            (<div className={css('free-minutes-section')}>
                                <label className={css('medium-font free-text')}>{Resources.FreeTierText}</label>
                                <label className={css('small-font')}>{Utils_String.format(Resources.TotalFreeMinutesFormat, concurrency.hostedAgentMinutesFreeCount)}</label>
                            </div>)
                        }
                        {
                            !showFreeTier &&
                            (<div className={css('value-header-section')}>
                                { !this.state.loadingPrivateMarketplaceData && <label className={css('extralarge-font')}>{this._getConcurrentJobsText(concurrency.totalHostedLicenseCount)}</label> }
                                { this.state.loadingPrivateMarketplaceData && <Spinner className={"value-section-spinner"} size={SpinnerSize.small} /> }
                                <label className={css('small-font')}>{Resources.ConcurrentJobsText}</label>
                            </div>)
                        }
                    </div>
                    {
                        showFreeTier &&
                        (<div className={css("concurrency-row")}>
                            <label className={css('medium-font')}>
                                {Utils_String.format(Resources.ConsumedFreeMinutesFormat, concurrency.hostedAgentMinutesUsedCount, concurrency.hostedAgentMinutesFreeCount)}
                            </label>
                            <Link className={css('medium-font purchase-jobs-link purchase-concurrentjobs-link')} target="_blank" href={this._privateProjectsMicrosoftHostedPurchaseLink} >{Resources.PurchaseConcurrentJobsText}</Link>
                        </div>)
                    }
                    {
                        !showFreeTier &&
                        (<div className={css("concurrency-row")}>
                            <label className={css('medium-font')}>
                                {Resources.MonthlyPurchasesText}
                            </label>
                            <div className={css('medium-font floatright-section')}>
                                { this.state.loadingPrivateMarketplaceData && <Spinner size={SpinnerSize.small} /> }
                                { !this.state.loadingPrivateMarketplaceData && <label className={css('medium-font concurrency-count')}>{this._getConcurrentJobsText(concurrency.purchasedHostedLicenseCount)}</label> }
                                { !this.state.loadingPrivateMarketplaceData && <Link className={css('medium-font purchase-jobs-link')} target="_blank" href={this._privateProjectsMicrosoftHostedPurchaseLink} >{Resources.ChangeText}</Link> }
                            </div>
                        </div>)
                    }
                </div>);
    }

    private _getPrivateProjectsSelfHostedConcurrencyView(): JSX.Element {
        const concurrency = this.state.taskHubLicenseDetails;
        return (<div>
            <div className={css('concurrency-row title-row')}>
                <div className={css('title-header-section')}>
                    <img className="concurrency-type-icon" src={this._selfHostedImageUrl} alt="" aria-label={Resources.SelfHostedConcurrencyTitle} />
                    <div className={css('title-and-link-section')}>
                        <div className={css('title-section')}>
                            <label className={css('large-font')}>{Resources.SelfHostedConcurrencyTitle}</label>
                            <InfoButton
                                        isIconFocusable={true}
                                        calloutContent={{
                                                            calloutDescription: Resources.SelfHostedConcurrencyInfoText,
                                                            calloutLink: this._selfHostedLearnMoreLink,
                                                            calloutLinkText: Resources.LearnMoreText
                                                        } as ICalloutContentProps} />
                        </div>
                        <Link className={css('small-font view-inprogress-jobs-link')} onClick={(event: React.MouseEvent<HTMLElement>) =>
                            this._showPrivateProjectsSelfHostedConcurrencyPanel(event.currentTarget)}>{Resources.ViewInProgressJobsText}</Link>
                    </div>
                </div>
                <div className={css('value-header-section')}>
                    <label className={css('extralarge-font')}>{this._getConcurrentJobsText(concurrency.totalPrivateLicenseCount)}</label>
                    <label className={css('small-font')}>{Resources.ConcurrentJobsText}</label>
                </div>
            </div>
            <div className={css("concurrency-row")}>
                <label className={css('medium-font free-text')}>{Resources.FreeText}</label>
                <label className={css('medium-font')}>{Resources.ConcurrentJobsSmallText}</label>
                <label className={css('medium-font concurrency-count floatright-section')}>{this._getConcurrentJobsText(concurrency.freeLicenseCount)}</label>
            </div>
            <div className={css("concurrency-row")}>
                <label className={css('medium-font')}>{Resources.VisualStudioEnterpriseSubscribersText}</label>
                <InfoButton
                    isIconFocusable={true}
                    calloutContent={{
                                        calloutDescription: Resources.VisualStudioEnterpriseUsersInfoText,
                                        calloutLink: this._msdnSubscribersLearnMoreLink,
                                        calloutLinkText: Resources.LearnMoreText
                                    } as ICalloutContentProps} />                            
                <label className={css('medium-font concurrency-count floatright-section')}>{this._getConcurrentJobsText(concurrency.enterpriseUsersCount)}</label>
            </div>
            <div className={css("concurrency-row")}>
                <label className={css('medium-font')}>{Resources.MonthlyPurchasesText}</label>
                <div className={css('medium-font floatright-section')}>
                    <label className={css('medium-font concurrency-count')}>{this._getConcurrentJobsText(concurrency.purchasedLicenseCount)}</label>
                    <Link className={css('medium-font purchase-jobs-link')} target="_blank" href={this._privateProjectSelftHostedPurchaseLink}>{Resources.ChangeText}</Link>
                </div>
            </div>
        </div>);
    }

    private _getPublicProjectsMicrosoftHostedConcurrencyView(): JSX.Element {
        if (this.state.publicProjectsMicrosoftHostedConcurrency === null) {
            return null;
        }

        const isLoading = this.state.publicProjectsMicrosoftHostedConcurrency === undefined;
        const concurrency = this.state.publicProjectsMicrosoftHostedConcurrency;
        const showPurchasedCount = !isLoading && concurrency.purchasedConcurrentJobsCount > 0;

        return (<div>
            <div className={css('concurrency-row title-row')}>
                <div className={css('title-header-section')}>
                    <img className="concurrency-type-icon" src={this._microsoftHostedImageUrl} alt="" aria-label={Resources.MicrosoftHostedConcurrencyTitle} />
                    <div className={css('title-and-link-section')}>
                        <div className={css('title-section')}>
                            <label className={css('large-font')}>{Resources.MicrosoftHostedConcurrencyTitle}</label>
                            <InfoButton
                                isIconFocusable={true}
                                calloutContent={{
                                                    calloutDescription: Resources.MicrosoftHostedConcurrencyInfoText,
                                                    calloutLink: this._microsoftHostedLearnMoreLink,
                                                    calloutLinkText: Resources.LearnMoreText                                                    
                                                } as ICalloutContentProps} />
                        </div>
                        <Link className={css('small-font view-inprogress-jobs-link')} onClick={(event: React.MouseEvent<HTMLElement>) => this._showPublicProjectsMicrosoftHostedConcurrencyPanel(event.currentTarget)}>{Resources.ViewInProgressJobsText}</Link>
                    </div>
                </div>
                <div className={css('value-header-section')}>
                    { this.state.publicProjectsMicrosoftHostedConcurrency && <label className={css('extralarge-font')}>{this._getConcurrentJobsText(this.state.publicProjectsMicrosoftHostedConcurrency.totalConcurrentJobsCount)}</label> }
                    { isLoading && <Spinner className={"value-section-spinner"} size={SpinnerSize.small} /> }
                    <label className={css('small-font')}>{Resources.ConcurrentJobsText}</label>
                </div>
            </div>
            {
                showPurchasedCount &&
                (<div className={css("concurrency-row")}>
                    <label className={css('medium-font free-text')}>{Resources.FreeText}</label>
                    <label className={css('medium-font')}>{Resources.ConcurrentJobsSmallText}</label>
                    <label className={css('medium-font concurrency-count floatright-section')}>{this._getConcurrentJobsText(concurrency.freeConcurrentJobsCount)}</label>
                </div>)
            }
            {          
                showPurchasedCount &&
                (<div className={css("concurrency-row")}>
                    <label className={css('medium-font')}>
                        {Resources.MonthlyPurchasesText}
                    </label>
                    <div className={css('medium-font floatright-section')}>
                        <label className={css('medium-font concurrency-count')}>{this._getConcurrentJobsText(concurrency.purchasedConcurrentJobsCount)}</label>
                        <Link className={css('medium-font purchase-jobs-link')} target="_blank" href={this._privateProjectsMicrosoftHostedPurchaseLink} >{Resources.ChangeText}</Link>
                    </div>
                </div>)
            }
        </div>);
    }

    private _getPublicProjectsSelfHostedConcurrencyView(): JSX.Element {
        if (this.state.publicProjectsSelfHostedConcurrency === null) {
            return null;
        }

        const isLoading = this.state.publicProjectsSelfHostedConcurrency === undefined;

        return (<div>
            <div className={css('concurrency-row title-row')}>
                <div className={css('title-header-section')}>
                    <img className="concurrency-type-icon" src={this._selfHostedImageUrl} alt="" aria-label={Resources.SelfHostedConcurrencyTitle} />
                    <div className={css('title-and-link-section')}>
                        <div className={css('title-section')}>
                            <label className={css('large-font')}>{Resources.SelfHostedConcurrencyTitle}</label>
                            <InfoButton
                                isIconFocusable={true}
                                calloutContent={{
                                                    calloutDescription: Resources.SelfHostedConcurrencyInfoText,
                                                    calloutLink: this._selfHostedLearnMoreLink,
                                                    calloutLinkText: Resources.LearnMoreText
                                                } as ICalloutContentProps} />
                        </div>
                        <Link className={css('small-font view-inprogress-jobs-link')} onClick={(event: React.MouseEvent<HTMLElement>) => this._showPublicProjectsSelfHostedConcurrencyPanel(event.currentTarget)}>{Resources.ViewInProgressJobsText}</Link>
                    </div>
                </div>
                <div className={css('value-header-section')}>
                    { !isLoading && <label className={css('extralarge-font')}>{this._getConcurrentJobsText(this.state.publicProjectsSelfHostedConcurrency.totalConcurrentJobsCount) }</label> }
                    { isLoading && <Spinner className={"value-section-spinner"} size={SpinnerSize.small} /> }
                    <label className={css('small-font')}>{Resources.ConcurrentJobsText}</label>
                </div>
            </div>
        </div>);
    }

    private _showInProgressJobs(projectsTitle: string, imgUrl: string, concurrencyTitle: string, parallelismTag: string, isHosted: boolean, elementToFocusOnDismiss: HTMLElement): void {
        this._httpClient.getResourceUsage(parallelismTag, isHosted, true).then((resourceUsage: DistributedTask.ResourceUsage) => {

            this._viewInprogressJobsPanelContainer = document.createElement("div");
            document.body.appendChild(this._viewInprogressJobsPanelContainer);

            let component = React.createElement(ViewInprogressJobsView, {
                projectsTitle: projectsTitle,
                imgUrl: imgUrl,
                concurrencyTitle: concurrencyTitle,
                resourceUsage: resourceUsage,
                onClosed: this._handleOnClosed,
                elementToFocusOnDismiss: elementToFocusOnDismiss
            });

            ReactDOM.render(component, this._viewInprogressJobsPanelContainer);

        },
        (error) => {
                alert(error.message || error);
        });
    }

    private _getConcurrentJobsText(concurrentJobs: number): string {
        if (concurrentJobs >= 10000) {
            return Resources.UnlimitedText;
        }
        return !!concurrentJobs ? concurrentJobs.toString() : "0";
    }

    private _handleOnClosed = () => {
        ReactDOM.unmountComponentAtNode(this._viewInprogressJobsPanelContainer);
        this._viewInprogressJobsPanelContainer.remove();
    }

    private _getBillingUrl(collectionUrl: string): string {
        return collectionUrl + "_settings/billing";
    }

    private _showConcurrencyPanel(isHosted: boolean, parallelismTag: string) {
        // ms hosted agents
        if (isHosted) {
            if (Utils_String.equals(DTConstants.ParallelismTagTypes.Private, parallelismTag, true)){
                this._showPrivateProjectsMicrosoftHostedConcurrencyPanel();
            } else if (Utils_String.equals(DTConstants.ParallelismTagTypes.Public, parallelismTag, true)) {
                this._showPublicProjectsMicrosoftHostedConcurrencyPanel();
            }
        // self hosted agents
        } else {
            if (Utils_String.equals(DTConstants.ParallelismTagTypes.Private, parallelismTag, true)) {
                this._showPrivateProjectsSelfHostedConcurrencyPanel();
            } else if (Utils_String.equals(DTConstants.ParallelismTagTypes.Public, parallelismTag, true)) {
                this._showPublicProjectsSelfHostedConcurrencyPanel();
            }
        }
    }

    private _showPrivateProjectsMicrosoftHostedConcurrencyPanel(eventTarget: HTMLElement = undefined): void {
        this._showInProgressJobs(Resources.PrivateProjectsLabel, this._microsoftHostedImageUrl, Resources.MicrosoftHostedConcurrencyTitle, DTConstants.ParallelismTagTypes.Private, true, eventTarget)
    }

    private _showPrivateProjectsSelfHostedConcurrencyPanel(eventTarget: HTMLElement = undefined): void {
        this._showInProgressJobs(Resources.PrivateProjectsLabel, this._selfHostedImageUrl, Resources.SelfHostedConcurrencyTitle, DTConstants.ParallelismTagTypes.Private, false, eventTarget)
    }

    private _showPublicProjectsMicrosoftHostedConcurrencyPanel(eventTarget: HTMLElement = undefined): void {
        this._showInProgressJobs(Resources.PublicProjectsLabel, this._microsoftHostedImageUrl, Resources.MicrosoftHostedConcurrencyTitle, DTConstants.ParallelismTagTypes.Public, true, eventTarget)
    }

    private _showPublicProjectsSelfHostedConcurrencyPanel(eventTarget: HTMLElement = undefined): void {
        this._showInProgressJobs(Resources.PublicProjectsLabel, this._selfHostedImageUrl, Resources.SelfHostedConcurrencyTitle, DTConstants.ParallelismTagTypes.Public, false, eventTarget)
    }

    private _httpClient: DistributedTaskApi.TaskAgentHttpClient;
    private _viewInprogressJobsPanelContainer: HTMLElement;
    private _privateProjectsMicrosoftHostedPurchaseLink: string;
    private _privateProjectSelftHostedPurchaseLink: string;
    private _microsoftHostedImageUrl: string;
    private _selfHostedImageUrl: string;

    private readonly _msdnSubscribersLearnMoreLink: string = "https://aka.ms/msdnsubscribers";
    private readonly _microsoftHostedLearnMoreLink: string = "https://aka.ms/microsofthosted";
    private readonly _selfHostedLearnMoreLink: string = "https://aka.ms/selfhosted";

    private readonly _isHosted: string = "isHosted";
    private readonly _parallelismTag: string = "parallelismTag";
}
